import { safePolygon, useHover, useInteractions, type FloatingContext } from "@floating-ui/react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

const SUBMENU_ROW_SELECTOR = "[data-menu-item-v2-row]";

const getAlignedSubmenuTop = (submenuLayer: HTMLDivElement) => {
  const firstRow = submenuLayer.querySelector<HTMLElement>(SUBMENU_ROW_SELECTOR);

  if (firstRow == null) return null;

  return Math.round(
    submenuLayer.getBoundingClientRect().top - firstRow.getBoundingClientRect().top,
  );
};

export const useAlignedSubmenuLayer = (submenuOpen: boolean) => {
  const submenuLayerRef = useRef<HTMLDivElement | null>(null);
  const [submenuTopOffset, setSubmenuTopOffset] = useState<number | null>(null);

  const updateSubmenuTop = useCallback((node: HTMLDivElement | null) => {
    submenuLayerRef.current = node;

    if (node == null) {
      setSubmenuTopOffset(null);
      return;
    }

    setSubmenuTopOffset(getAlignedSubmenuTop(node));
  }, []);

  useLayoutEffect(() => {
    if (!submenuOpen || submenuLayerRef.current == null) return;

    setSubmenuTopOffset(getAlignedSubmenuTop(submenuLayerRef.current));
  }, [submenuOpen]);

  return { submenuTopOffset, updateSubmenuTop };
};

// Hover interactions for a row's flyout submenu. `safePolygon` — the
// same Floating UI helper behind the sidebar pullouts (TooltipV2's
// `persistOnHover`) — keeps the submenu open while the cursor travels
// the diagonal from the parent row toward the submenu, instead of
// snapping shut the moment the pointer leaves the row/submenu
// rectangles. Moving off the diagonal (e.g. down to a sibling row)
// still closes immediately.
export const useSubmenuHoverInteractions = (context: FloatingContext, enabled: boolean) => {
  const hover = useHover(context, {
    enabled,
    handleClose: safePolygon(),
  });

  return useInteractions([hover]);
};
